import { STORED_COUNTRY_NAMES } from "./addressCountry";
// Keys are uppercased; lookup normalises via .trim().toUpperCase() so "fr",
// "France", "FRANCE " all resolve correctly.
// CA_FR is selected when the province is QC (Québec) — the only province where
// French is the sole official language. All other Canadian addresses use CA_EN.
const COUNTRY_FORMAT_MAP = {
    FR: "FR", FRANCE: "FR",
    NL: "NL", "PAYS-BAS": "NL", NETHERLANDS: "NL",
    CA: "CA_EN", CANADA: "CA_EN",
};
export function detectCountryFormat(country, region) {
    if (!country)
        return "generic";
    const code = COUNTRY_FORMAT_MAP[country.trim().toUpperCase()] ?? "generic";
    if (code === "CA_EN" && region?.trim().toUpperCase() === "QC")
        return "CA_FR";
    return code;
}
export function formatAddress(address, countryCode, names = STORED_COUNTRY_NAMES) {
    return formatAddressParts(address, countryCode, names).join(", ");
}
// CONSTRAINT: `streetName` is read only for rows saved before OP-8910 bound every street to line1;
// OP-8957 migrates them and drops this fallback.
export function formatStreetLine(address, countryCode) {
    const street = address.line1 || address.streetName || "";
    const house = address.houseNumber ? address.houseNumber + (address.houseNumberSuffix ?? "") : "";
    const numberLast = countryCode === "NL";
    // Rows saved before OP-8051 can hold a numbered line1 next to the same house number.
    const words = street.split(/\s+/);
    if (house && (numberLast ? words.at(-1) : words[0]) === house)
        return street;
    const ordered = numberLast ? [street, house] : [house, street];
    return ordered.filter(Boolean).join(" ");
}
function streetLines(address, countryCode) {
    const complements = [address.line2, address.line3].filter((l) => !!l);
    return [formatStreetLine(address, countryCode), ...complements].filter(Boolean);
}
// Same country-ordered segments as formatAddress, unjoined — the edit modals
// render them one per line as the multi-line preview.
export function formatAddressParts(address, countryCode, names = STORED_COUNTRY_NAMES) {
    const parts = [];
    const country = address.country ? names.label(address.country) : "";
    const street = streetLines(address, countryCode);
    const [firstLine, ...otherLines] = street;
    switch (countryCode) {
        case "FR": {
            // [street lines], [postalCode city], [country]
            parts.push(...street);
            const pcCity = [address.postalCode, address.city].filter(Boolean).join(" ");
            if (pcCity)
                parts.push(pcCity);
            if (country)
                parts.push(country);
            break;
        }
        case "CA_EN": {
            // [first street line city][, other street lines], [region postalCode], [country]
            const lineCity = [firstLine, address.city].filter(Boolean).join(" ");
            if (lineCity)
                parts.push(lineCity);
            parts.push(...otherLines);
            const regionPc = [address.region, address.postalCode].filter(Boolean).join(" ");
            if (regionPc)
                parts.push(regionPc);
            if (country)
                parts.push(country);
            break;
        }
        case "CA_FR": {
            // [first street line city][, other street lines], ([region]) [postalCode], [country]
            const lineCity = [firstLine, address.city].filter(Boolean).join(" ");
            if (lineCity)
                parts.push(lineCity);
            parts.push(...otherLines);
            const regionPart = address.region ? `(${address.region})` : "";
            const regionPc = [regionPart, address.postalCode].filter(Boolean).join(" ");
            if (regionPc)
                parts.push(regionPc);
            if (country)
                parts.push(country);
            break;
        }
        case "NL": {
            // [street lines], [postalCode city], [country]
            parts.push(...street);
            const pcCity = [address.postalCode, address.city].filter(Boolean).join(" ");
            if (pcCity)
                parts.push(pcCity);
            if (country)
                parts.push(country);
            break;
        }
        default: {
            // generic: [street lines], [city postalCode], [region], [country]
            parts.push(...street);
            const cityPc = [address.city, address.postalCode].filter(Boolean).join(" ");
            if (cityPc)
                parts.push(cityPc);
            if (address.region)
                parts.push(address.region);
            if (country)
                parts.push(country);
            break;
        }
    }
    return parts;
}
